import { memo, useState, useCallback, useMemo, useRef, useLayoutEffect, Fragment } from "react";
import MathText from '../MathText';
import { useQuizTheme, answerTheme } from './quizTheme';
import { etatsTrous } from './corrige';
import { preprocessBraces, buildMathHtml, slotLatex, ETAI_TROU, enLignes, enChaine, enTableau } from '../texteTrouMath';

/**
 * Composant interactif "Texte à trous" côté élève.
 * Mode drag : tap un mot proposé → va dans le prochain trou. Tap trou rempli → retourne.
 * Mode saisie : inputs dans chaque trou.
 *
 * @param {string} phrase - Phrase avec trous "Un triangle {{équilatéral}}..." (format contenu.phrase)
 * @param {string} mode - 'drag' ou 'saisie' (format contenu.mode)
 * @param {Array} distracteurs - Mots distracteurs ["isocèle", ...] (format contenu.distracteurs)
 * @param {string} selectedValue - Réponse existante (phrase remplie) pour restauration
 * @param {Function} onAnswer - Callback (phrase avec trous remplis)
 * @param {Object|null} corrige - Correction sur place (lot 4, 2026-09-21), voir plus bas
 */
const TexteTrouEleve = memo(function TexteTrouEleve({ phrase: phraseProp, mode: modeProp, distracteurs, selectedValue, onAnswer, onComplete, corrige = null }) {
    const clair = useQuizTheme() === 'clair';
    const t = answerTheme(clair);

    // ─── Correction sur place (lot 4, 2026-09-21) ───
    // `corrige` n'arrive que si la réponse est fausse (contrat dans corrige.jsx).
    // Par trou : ce qu'il a mis, ce qu'il fallait, juste ou faux par la règle
    // même du scoring (tolérance comprise). Les trous se figent, en lecture
    // seule, et prennent les classes juste / faux d'answerTheme. Mode texte :
    // la bonne valeur se pose SOUS un trou faux, collée à lui, la phrase garde
    // sa forme ; un trou juste devient simplement vert. Mode équation : les cases
    // se colorent en place, et SOUS la formule, la même formule réécrite avec
    // les bonnes valeurs (« La réponse ») : même disposition KaTeX, donc chaque
    // bonne valeur tombe exactement sous sa case. Une case posée au pixel n'a
    // aucune place à côté d'elle, et une ligne de pastilles « ce qu'il a mis →
    // la bonne » devenait ambiguë dès qu'une valeur revenait (deux « 45 »,
    // constat prof 2026-09-21 sur huit cases). Le bac d'étiquettes disparaît.
    // Sans `corrige`, le rendu d'avant, inchangé.
    // Ce qui s'affiche dans un trou corrigé est `mot`, ce que Quiz a jugé, et
    // non l'état restauré depuis la réponse sauvée : cette restauration saute
    // un trou vidé et décale les suivants d'un cran (défaut d'avant, hors lot).
    const etats = useMemo(() => (corrige ? etatsTrous(corrige) : null), [corrige]);
    const trou = (i) => (etats ? { etat: etats[i]?.juste ? 'juste' : 'faux', attendu: etats[i]?.attendu ?? '', mot: etats[i]?.mot ?? '' } : null);
    const phrase = phraseProp || '';
    const mode = modeProp || 'drag';

    // Parser la phrase en segments (texte + trous)
    const { segments, answerWords } = useMemo(() => {
        const safe = preprocessBraces(phrase);
        const parts = [];
        const answers = [];
        let lastIndex = 0;
        const re = /\{\{([^}]+)\}\}/g;
        let m;

        while ((m = re.exec(safe)) !== null) {
            if (m.index > lastIndex) {
                parts.push({ type: 'text', content: safe.substring(lastIndex, m.index) });
            }
            parts.push({ type: 'slot', content: m[1].trim(), slotIndex: answers.length });
            answers.push(m[1].trim());
            lastIndex = m.index + m[0].length;
        }
        if (lastIndex < safe.length) {
            parts.push({ type: 'text', content: safe.substring(lastIndex) });
        }

        return { segments: parts, answerWords: answers };
    }, [phrase]);

    // Groupes insécables (2026-08-01) : un NBSP au bord d'un trou signifie
    // « ne pas couper entre ce texte et le champ ». Les navigateurs coupent
    // librement autour d'un inline-block même adjacent à un NBSP — la glue
    // est donc matérialisée en enveloppant chaque chaîne texte↔trou reliée
    // par des NBSP dans un span whitespace-nowrap. Sans NBSP au bord des
    // trous, le découpage est inchangé (aucun impact sur les phrases legacy).
    const rendu = useMemo(() => {
        const NB = '\u00a0';
        const out = [];
        let groupe = null;
        const fermer = () => { if (groupe) { out.push({ type: 'group', items: groupe }); groupe = null; } };
        for (const seg of segments) {
            if (seg.type !== 'text') {
                if (!groupe) groupe = [];
                groupe.push(seg);
                continue;
            }
            const c = seg.content;
            let reste = c;
            if (groupe) {
                if (c.startsWith(NB)) {
                    const premiereCoupe = c.search(/[ \n]/);
                    if (premiereCoupe === -1) { groupe.push({ type: 'text', content: c }); continue; }
                    groupe.push({ type: 'text', content: c.slice(0, premiereCoupe) });
                    reste = c.slice(premiereCoupe);
                }
                fermer();
            }
            const derniereCoupe = Math.max(reste.lastIndexOf(' '), reste.lastIndexOf('\n'));
            if (reste.endsWith(NB)) {
                const queue = reste.slice(derniereCoupe + 1);
                const milieu = reste.slice(0, derniereCoupe + 1);
                if (milieu) out.push({ type: 'text', content: milieu });
                groupe = [{ type: 'text', content: queue }];
            } else if (reste) {
                out.push({ type: 'text', content: reste });
            }
        }
        fermer();
        return out;
    }, [segments]);

    // Mots proposés mélangés (réponses extraites des trous + distracteurs)
    const proposedWords = useMemo(() => {
        const words = [...answerWords, ...(distracteurs || [])];
        const shuffled = [...words];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    }, [answerWords, distracteurs]);

    // State : mot placé dans chaque trou (index → mot)
    // Restaurer les trous remplis depuis la réponse sauvée (drag ET saisie)
    const [filledSlots, setFilledSlots] = useState(() => {
        if (!selectedValue) return {};
        const filled = {};
        const re = /\{\{([^}]+)\}\}/g;
        let m, idx = 0;
        while ((m = re.exec(selectedValue)) !== null) {
            filled[idx] = m[1].trim();
            idx++;
        }
        return Object.keys(filled).length > 0 ? filled : {};
    });

    // Pas de resync sur selectedValue : l'état est lu UNE FOIS au montage,
    // le montage est keyé par question (Quiz / ExerciseurPreview).

    // Construire la phrase avec les trous remplis pour onAnswer
    const buildAnswer = useCallback((slots) => {
        const isComplete = Object.keys(slots).length >= answerWords.length;
        onComplete(isComplete);
        if (!isComplete) return;
        // Reconstruire la phrase en remplaçant chaque trou par le mot placé
        const re = /\{\{([^}]+)\}\}/g;
        const matches = [];
        let m;
        while ((m = re.exec(phrase)) !== null) matches.push(m);

        let built = '';
        let lastIdx = 0;
        matches.forEach((match, i) => {
            built += phrase.substring(lastIdx, match.index);
            built += `{{${slots[i] || ''}}}`;
            lastIdx = match.index + match[0].length;
        });
        built += phrase.substring(lastIdx);

        onAnswer(built);
    }, [phrase, answerWords.length, onAnswer, onComplete]);

    // ─── Mode drag : tap mot → place dans le prochain trou ───

    // Mots encore disponibles (pas placés dans un trou)
    const availableWords = useMemo(() => {
        if (mode !== 'drag') return [];
        const used = Object.values(filledSlots);
        const remaining = [...proposedWords];
        for (const w of used) {
            const idx = remaining.indexOf(w);
            if (idx !== -1) remaining.splice(idx, 1);
        }
        return remaining;
    }, [mode, proposedWords, filledSlots]);

    // Les trois gestes calculent la nouvelle valeur ICI, dans le gestionnaire,
    // et donnent la même à setState et au parent (buildAnswer), juste après.
    // Jamais dans la fonction passée à setState : React se réserve de la
    // rejouer (deux fois en StrictMode, autant qu'il veut si le rendu est
    // interrompu), et chaque rejeu prévenait Quiz une fois de plus, boucle
    // vécue sur l'horloge le 21/09 (« Maximum update depth exceeded »).
    const handleWordClick = useCallback((word) => {
        // Trouver le prochain trou vide
        const nextEmpty = answerWords.findIndex((_, i) => !filledSlots[i]);
        if (nextEmpty === -1) return;
        const next = { ...filledSlots, [nextEmpty]: word };
        setFilledSlots(next);
        buildAnswer(next);
    }, [answerWords, filledSlots, buildAnswer]);

    const handleSlotClick = useCallback((slotIndex) => {
        if (!filledSlots[slotIndex]) return;
        const next = { ...filledSlots };
        delete next[slotIndex];
        setFilledSlots(next);
        buildAnswer(next);
    }, [filledSlots, buildAnswer]);

    // ─── Mode saisie : input dans chaque trou ───

    const handleInputChange = useCallback((slotIndex, value) => {
        const next = { ...filledSlots, [slotIndex]: value };
        setFilledSlots(next);
        buildAnswer(next);
    }, [filledSlots, buildAnswer]);

    // ─── Mode équation (KaTeX global) : les trous sont DANS une équation ───
    // mathHtml ≠ null ⇒ l'énoncé contient une ligne $...$ : on rend l'équation
    // entière puis on superpose des champs sur chaque trou (placeholders .tt-slot-N).
    // L'étai est HORS du \htmlClass : la boîte mesurée reste celle du \phantom,
    // seule la barre de fraction s'écarte (cf. ETAI_TROU).
    const mathHtml = useMemo(
        () => buildMathHtml(phrase, (idx, contenu) => {
            // Slot dimensionné sur le mot attendu, borné 3..14 : le champ
            // superposé impose 36 px de large minimum — un fantôme de 2
            // chiffres (~20 px) était plus étroit que lui et le champ
            // recouvrait le symbole voisin (× collé au cadre, constat
            // utilisateur gen_1005, 2026-08-19). Les \; de part et d'autre
            // sont la GARDE : elles appartiennent à la mise en page KaTeX,
            // c'est donc le layout lui-même qui écarte les voisins de la
            // zone où le champ vivra (l'enveloppe du champ déborde d'au
            // plus ~3 px par côté, la garde en réserve ~5).
            const larg = Math.min(Math.max(3, String(contenu || '').trim().length), 14);
            return `\\;${ETAI_TROU}\\htmlClass{tt-slot tt-slot-${idx}}{\\phantom{${'0'.repeat(larg)}}}\\;`;
        }),
        [phrase]
    );
    // La formule attendue (correction, mode équation) : la bonne valeur de
    // chaque trou dans le même KaTeX. Un trou faux la porte en .tt-attendu
    // (souligné pointillé vert, jamais une boîte à bordure : KaTeX ne la
    // connaît pas et la barre de fraction se retrouverait dessous), un trou
    // juste la porte en encre normale, il n'y a rien à corriger.
    const mathHtmlAttendu = useMemo(() => {
        if (!etats || !mathHtml) return null;
        return buildMathHtml(phrase, (i, contenu) => {
            const e = etats[i];
            const valeur = slotLatex(e?.attendu || contenu);
            return e && !e.juste ? `\\htmlClass{tt-attendu}{${valeur}}` : valeur;
        });
    }, [etats, mathHtml, phrase]);
    const containerRef = useRef(null);
    const [slotRects, setSlotRects] = useState([]);

    useLayoutEffect(() => {
        if (!mathHtml) { setSlotRects([]); return undefined; }
        const cont = containerRef.current;
        if (!cont) return undefined;
        const measure = () => {
            const cb = cont.getBoundingClientRect();
            const rects = [];
            for (let i = 0; i < answerWords.length; i++) {
                const el = cont.querySelector(`.tt-slot-${i}`);
                if (el) {
                    const r = el.getBoundingClientRect();
                    rects[i] = { left: r.left - cb.left, top: r.top - cb.top, width: r.width, height: r.height };
                } else {
                    rects[i] = null;
                }
            }
            setSlotRects(rects);
        };
        measure();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(measure);
        ro.observe(cont);
        return () => ro.disconnect();
    }, [mathHtml, answerWords.length]);

    if (!phrase) return null;

    // Un trou corrigé du mode texte : une colonne posée dans le fil, le champ
    // ou l'étiquette en haut, figé, avec ce qu'il a mis (rouge et ✗ si faux,
    // vert si juste), et, sous un trou faux, la bonne valeur en pointillé,
    // collée à lui. Rien n'est inséré dans la ligne : la phrase garde sa forme,
    // seule la hauteur de la ligne grandit là où il y a une erreur (première
    // version, la pastille APRÈS le trou : elle passait à la ligne, séparée de
    // son trou, et « 15 h 65 ✗ 00 » ne se lisait plus, constat prof 2026-09-21).
    // La pastille appartient à SA case par la proximité : 2 px au-dessus d'elle,
    // 12 px en dessous, pour qu'elle ne se lise jamais comme la correction de
    // la case qui ouvre la ligne suivante (constat prof, même jour).
    const renduTrouCorrige = (i, key) => {
        const { etat, attendu, mot } = trou(i);
        return (
            <span key={key} className="inline-flex flex-col items-start gap-0.5 mx-1 align-baseline">
                {mode === 'drag' ? (
                    <button
                        type="button"
                        disabled
                        data-testid={`trou-${i}`}
                        data-etat={etat}
                        className={`inline-flex items-center justify-center gap-1 min-w-[60px] px-2 py-0.5 rounded-lg border-2 font-medium cursor-default ${t[etat]}`}
                    >
                        {mot ? <MathText>{mot}</MathText> : '…'}
                        {etat === 'faux' ? <span aria-hidden="true">✗</span> : null}
                    </button>
                ) : (
                    <span className="inline-flex items-center gap-1">
                        <input
                            type="text"
                            readOnly
                            value={mot}
                            placeholder="vide"
                            aria-label="Ta réponse"
                            data-testid={`trou-${i}`}
                            data-etat={etat}
                            className={`inline-block w-16 sm:w-24 px-1 sm:px-2 py-0.5 rounded-lg border-2 font-medium text-center focus:outline-none ${t[etat]}`}
                        />
                        {etat === 'faux' ? <span aria-hidden="true" className="font-black text-red-600">✗</span> : null}
                    </span>
                )}
                {etat === 'faux' ? (
                    <span
                        data-testid={`attendu-${i}`}
                        className={`inline-flex items-center justify-center mb-3 px-2 py-0.5 rounded-lg border-2 font-bold ${mode === 'drag' ? 'min-w-[60px]' : 'min-w-[4rem] sm:min-w-[6rem]'} ${t.attendu}`}
                    >
                        <MathText>{attendu}</MathText>
                    </span>
                ) : null}
            </span>
        );
    };

    // Un item d'une ligne du mode classique : texte, ou groupe insécable
    // (texte collé par NBSP + trou), le trou étant un champ (saisie) ou un
    // bouton (drag), ou le trou corrigé.
    const renduItem = (item, i) => (
        item.type === 'text' ? (
            <MathText key={i}>{item.content}</MathText>
        ) : (
            <span key={i} className="whitespace-nowrap">
                {item.items.map((seg, j) =>
                    seg.type === 'text' ? (
                        <MathText key={j}>{seg.content}</MathText>
                    ) : etats ? (
                        renduTrouCorrige(seg.slotIndex, j)
                    ) : mode === 'drag' ? (
                        // Mode drag : trou cliquable
                        <button
                            key={j}
                            type="button"
                            onClick={() => handleSlotClick(seg.slotIndex)}
                            className={`inline-flex items-center justify-center min-w-[60px] mx-1 px-2 py-0.5 rounded-lg border-2 transition-all ${
                                filledSlots[seg.slotIndex]
                                    ? `${t.selected} font-medium ${t.removeHover}`
                                    : `border-dashed ${t.slotEmptyBorder} ${t.slotEmptyText}`
                            }`}
                        >
                            {filledSlots[seg.slotIndex] ? <MathText>{filledSlots[seg.slotIndex]}</MathText> : '   '}
                        </button>
                    ) : (
                        // Mode saisie : input inline
                        <input
                            key={j}
                            type="text"
                            value={filledSlots[seg.slotIndex] || ''}
                            onChange={(e) => handleInputChange(seg.slotIndex, e.target.value)}
                            placeholder="..."
                            className={`inline-block w-16 sm:w-24 mx-1 px-1 sm:px-2 py-0.5 rounded-lg border-2 font-medium text-center focus:outline-none ${t.input}`}
                        />
                    )
                )}
            </span>
        )
    );

    // Une ligne : ses items à la suite ; une CHAÎNE d'égalités (enChaine) :
    // tête + membres « = … » insécables, repli seulement au « = » (.tt-chaine,
    // index.css, constat prof sur 1059 au téléphone, 2026-09-09).
    const renduLigne = (ligne, li) => {
        const chaine = enChaine(ligne);
        if (!chaine) return <div key={li} className="tt-ligne">{ligne.map(renduItem)}</div>;
        return (
            <div key={li} className="tt-ligne tt-chaine">
                <span>{chaine.tete.map(renduItem)}</span>
                <span className="tt-chaine-membres">
                    {chaine.membres.map((m, k) => <span key={k} className="tt-membre">{m.map(renduItem)}</span>)}
                </span>
            </div>
        );
    };

    return (
        <>
            {/* Pas de bandeau d'instruction : champs et étiquettes sont
                auto-explicites (épure 2026-07-31). */}
            {/* Phrase avec trous */}
            {mathHtml ? (
                /* Mode \u00E9quation : KaTeX global + champs superpos\u00E9s sur les trous */
                <div
                    ref={containerRef}
                    className={`relative px-4 py-4 rounded-xl border-2 ${t.dropzone} ${clair ? 'text-slate-800' : 'text-slate-200'} mt-3 sm:mt-4 [&_.katex]:text-inherit`}
                >
                    <div dangerouslySetInnerHTML={{ __html: mathHtml }} />
                    {slotRects.map((r, i) => {
                        if (!r) return null;
                        // Taille calée sur la case (mini réduit) + zIndex → évite le chevauchement
                        // des champs num/dénominateur d'une fraction (cases proches verticalement).
                        //
                        // Le champ s'élargit aussi avec CE QUI EST ÉCRIT : le gabarit borne
                        // la case à 14 caractères, au-delà la valeur était rognée (« 25789 »
                        // dans 36 px, retour utilisateur 2026-08-02).
                        // En correction : la case se colore en place (juste / faux), se fige,
                        // et montre ce que Quiz a jugé.
                        const c = etats ? trou(i) : null;
                        const contenu = c ? c.mot : (filledSlots[i] || '');
                        const wTexte = contenu.length * 8.5 + 16;
                        const w = Math.max(r.width + 6, 36, wTexte);
                        const h = Math.max(r.height + 4, 24);
                        const style = { left: r.left + r.width / 2 - w / 2, top: r.top + r.height / 2 - h / 2, width: w, height: h, zIndex: 5 };
                        const etatCase = c ? c.etat : null;
                        return mode === 'saisie' ? (
                            <input
                                key={i}
                                type="text"
                                value={contenu}
                                onChange={(e) => handleInputChange(i, e.target.value)}
                                readOnly={Boolean(etatCase)}
                                data-testid={etatCase ? `trou-${i}` : undefined}
                                data-etat={etatCase || undefined}
                                className={`absolute text-center font-bold text-sm rounded-md border-2 focus:outline-none ${etatCase ? t[etatCase] : t.input}`}
                                style={style}
                            />
                        ) : (
                            <button
                                key={i}
                                type="button"
                                disabled={Boolean(etatCase)}
                                data-testid={etatCase ? `trou-${i}` : undefined}
                                data-etat={etatCase || undefined}
                                onClick={() => handleSlotClick(i)}
                                className={`absolute flex items-center justify-center text-sm font-bold rounded-md border-2 transition-all ${
                                    etatCase
                                        ? `${t[etatCase]} cursor-default`
                                        : filledSlots[i]
                                            ? `${t.selected} ${t.removeHover}`
                                            : `border-dashed ${t.slotEmptyBorder} ${t.slotEmptyText} ${clair ? 'bg-white' : 'bg-slate-700'}`
                                }`}
                                style={style}
                            >
                                {contenu ? <MathText>{contenu}</MathText> : ''}
                            </button>
                        );
                    })}
                    {/* Correction : la même formule, avec les bonnes valeurs, sous la sienne */}
                    {mathHtmlAttendu ? (
                        <div className="mt-4" data-testid="reponse-attendue">
                            <span className="block mb-1 text-[11px] font-bold uppercase tracking-wide text-emerald-700">La réponse</span>
                            <div
                                className={`[&_.katex]:text-inherit [&_.tt-attendu]:font-extrabold [&_.tt-attendu]:border-b-2 [&_.tt-attendu]:border-dashed [&_.tt-attendu]:border-emerald-500 [&_.tt-attendu]:px-0.5 ${clair ? '[&_.tt-attendu]:text-emerald-700' : '[&_.tt-attendu]:text-emerald-300'}`}
                                dangerouslySetInnerHTML={{ __html: mathHtmlAttendu }}
                            />
                        </div>
                    ) : null}
                </div>
            ) : (
                /* Mode classique : segments texte / trou altern\u00E9s, les
                   cha\u00EEnes reli\u00E9es par NBSP restant sur une seule ligne */
                /* Une LIGNE de la phrase = un bloc (enLignes) : une grande
                   fraction verticale (\dfrac) déborde de l'interligne, sur
                   plusieurs lignes les fractions se chevauchaient (gen_600,
                   gen_923, retours écran 2026-09-06). Avec des fractions, les
                   blocs reçoivent en plus un dégagement (.texte-trou--fractions). */
                <div className={`px-4 py-3 rounded-xl border-2 ${t.dropzone} text-sm sm:text-base ${clair ? 'text-slate-800' : 'text-slate-200'} ${/\\dfrac/.test(phrase || '') ? 'texte-trou--fractions' : 'leading-10'} mt-3 sm:mt-4`}>
                    {(() => {
                        const lignes = enLignes(rendu);
                        // toutes les lignes en « libellé : contenu » → deux colonnes alignées
                        const tableau = enTableau(lignes);
                        if (!tableau) return lignes.map(renduLigne);
                        return (
                            <div className="tt-tableau">
                                {tableau.map(({ gauche, droite }, li) => (
                                    <Fragment key={li}>
                                        <span>{gauche.map(renduItem)}</span>
                                        <span>{droite.map(renduItem)}</span>
                                    </Fragment>
                                ))}
                            </div>
                        );
                    })()}
                </div>
            )}

            {/* Mots proposés — mode drag uniquement, et jamais en correction : la
                bonne étiquette restée au bac est dite par la pastille, pas par le bac */}
            {mode === 'drag' && !etats && availableWords.length > 0 && (
                <div className="flex flex-wrap gap-2 justify-center mt-3">
                    {availableWords.map((word, i) => (
                        <button
                            key={`${word}-${i}`}
                            type="button"
                            onClick={() => handleWordClick(word)}
                            className={`px-4 py-2 rounded-xl border-2 text-sm sm:text-base font-medium cursor-pointer hover:scale-105 transition-all shadow-sm ${t.chip}`}
                        >
                            <MathText>{word}</MathText>
                        </button>
                    ))}
                </div>
            )}
        </>
    );
});

export default TexteTrouEleve;
